import {
  Autocomplete,
  Box,
  Container,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Button,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import React, { useState, useEffect } from "react";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import useApiEp from "../../hooks/useApiEp";
import {
  exportToExcel,
  getContactColumnNames,
  getErrorResponse,
  getHeader,
} from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import SectionLabel from "../../widgets/SectionLabel";
import ImportOrManual from "../../widgets/ImportOrManual";
import ExcelImportDialog from "../../widgets/ExcelImportDialog";
import DynamicTableDialog from "../../widgets/DynamicTableDialog";
import { useDialog } from "../../context/DialogContext";
import { toast } from "react-toastify";

const AddContactToGroup = () => {
  const { showDialog } = useDialog();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [open, setOpen] = useState(false);
  const [openDialogForm, setOpenDialogForm] = useState(false);
  const [contactData, setContactData] = useState({
    data: null,
    isLoading: false,
    error: null,
  });

  // ── NEW: delete state ──────────────────────────────────────────────────────
  const [deleteState, setDeleteState] = useState({
    isLoading: false,
    error: null,
    success: null,
  });

  const {
    data: contactList,
    isLoading: loadingList,
    isError: isListError,
    error: listError,
  } = useApiEp("/user/group-list");

  useEffect(() => {
    if (!selectedGroup) return;

    const fetchContacts = async () => {
      setContactData((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await axios.get(
          `${API_ENDPOINT}/user/get-contact-schema?groupName=${encodeURIComponent(
            selectedGroup
          )}`,
          getHeader()
        );
        const schemaList = getContactColumnNames(response.data);

        setContactData({ data: schemaList, isLoading: false, error: null });
      } catch (err) {
        setContactData({
          data: null,
          isLoading: false,
          error: getErrorResponse(err) || "Failed to load contacts",
        });
      }
    };

    fetchContacts();
  }, [selectedGroup]);

  const handleGroupChange = (_, newValue) => {
    setSelectedGroup(newValue);
    setContactData({ data: null, isLoading: false, error: null });
    setDeleteState({ isLoading: false, error: null, success: null }); // reset on group change
  };

  // ── NEW: delete handler ────────────────────────────────────────────────────
  const handleDeleteGroup = async (groupName) => {
    setDeleteState({ isLoading: true, error: null, success: null });
    try {
      await axios.delete(`${API_ENDPOINT}/api/delete-contacts-in-group`, {
        ...getHeader(),
        data: { groupName: groupName.trim() },
      });
      const msg = `All contacts in "${groupName}" were deleted successfully.`;
      toast.success(msg);
      setDeleteState({
        isLoading: false,
        error: null,
        success: msg,
      });
    } catch (err) {
      setDeleteState({
        isLoading: false,
        error: getErrorResponse(err) || "Failed to delete contacts.",
        success: null,
      });
    }
  };

  // ── NEW: opens the confirmation dialog, then calls handleDeleteGroup ───────
  const handleDeleteClick = () => {
    if (!selectedGroup) return;
    showDialog(
      "Please confirm!",
      "Are you sure you want to delete all contacts in this group? Remember this is irrevocable!",
      {
        type: "delete",
        onConfirm: async () => {
          await handleDeleteGroup(selectedGroup);
        },
      }
    );
  };
  // ──────────────────────────────────────────────────────────────────────────

  const validateContacts = (rows) => {
    if (!Array.isArray(rows)) return "Invalid data!";
    if (rows.length < 1) return "At least one row is required!";
    const columns = Object.keys(rows[0]);
    if (!columns.includes("phoneNumber"))
      return "Phone number column must be included!";
    const hasEmptyPhone = rows.some((item) => !String(item.phoneNumber).trim());
    if (hasEmptyPhone) return "All phone numbers must be non-empty!";
    return null;
  };

  const getImportPayload = (rows) => ({
    contacts: rows,
    groupName: selectedGroup.trim(),
  });

  return (
    <Container maxWidth="md" sx={{ mt: 3 }}>
      <BackBtn />
      <PageTitle>Add Contact List to group</PageTitle>

      <Autocomplete
        size="small"
        loading={loadingList}
        sx={{ minWidth: 164, my: 3 }}
        options={contactList || []}
        value={selectedGroup}
        onChange={handleGroupChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Group Name"
            helperText={
              selectedGroup ? "" : "Select a contact group to view members"
            }
          />
        )}
      />

      {contactData.isLoading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress size={24} />
        </Box>
      )}

      {contactData.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {contactData.error}
        </Alert>
      )}

      {/* ── NEW: delete feedback alerts ── */}
      {deleteState.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteState.error}
        </Alert>
      )}
      {deleteState.success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {deleteState.success}
        </Alert>
      )}

      {selectedGroup && !contactData.isLoading && (
        <Box>
          <SectionLabel sx={{ my: 1 }}>Column names</SectionLabel>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {contactData?.data?.map((item) => (
              <Chip key={item} label={item} variant="outlined" clickable />
            ))}
          </Stack>

          <ImportOrManual
            onDownloadTemplate={() => {
              if (selectedGroup && contactData?.data?.length > 0) {
                exportToExcel(
                  [contactData.data],
                  "import_contact_template.xlsx",
                  true
                );
              }
            }}
            onImportExcel={() => setOpen(true)}
            onManualAdd={() => setOpenDialogForm(true)}
          />

          {/* ── NEW: Delete all contacts button ── */}
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={
                deleteState.isLoading ? (
                  <CircularProgress size={16} color="error" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              disabled={deleteState.isLoading}
              onClick={handleDeleteClick}
            >
              {deleteState.isLoading ? "Deleting…" : "Delete All Contacts"}
            </Button>
          </Box>

          <ExcelImportDialog
            open={open}
            onClose={() => setOpen(false)}
            url={`${API_ENDPOINT}/multipart/import-excel-to-contact-group`}
            statusBaseUrl={`${API_ENDPOINT}/user/import-job-status`}
            formFields={{ groupName: selectedGroup.trim() }}
            onSuccess={() => {}}
          />

          <DynamicTableDialog
            open={openDialogForm}
            onClose={() => setOpenDialogForm(false)}
            columnNames={contactData?.data || []}
            title="Add Contacts"
            validateInput={validateContacts}
            url={`${API_ENDPOINT}/user/import-excel-to-contact-group`}
            getPayload={getImportPayload}
          />
        </Box>
      )}
    </Container>
  );
};

export default AddContactToGroup;
